import { memo, ReactNode, useRef, useState } from 'react';

export type AccordionSectionProps = {
  title: string;
  count: number;
  icon: string;
  iconColor: string;
  backgroundColor: string;
  defaultExpanded?: boolean;
  headerExtra?: ReactNode;
  children: ReactNode;
};

export const AccordionSection = memo(
  ({
    title,
    count,
    icon,
    iconColor,
    backgroundColor,
    defaultExpanded = false,
    headerExtra,
    children,
  }: AccordionSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [isAnimating, setIsAnimating] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
      if (!isExpanded) {
        setIsAnimating(true);
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
        setTimeout(() => setIsAnimating(false), 200);
      }
    };

    const maxHeight = count > 10 ? '500px' : `${count * 50 + 10}px`;
    const animationDuration = count > 10 ? 150 : 300;

    return (
      <div className="mb-2">
        <div
          className={`flex cursor-pointer items-center gap-1.5 rounded p-2 ${backgroundColor}`}
          onClick={handleToggle}
        >
          {headerExtra}
          <i className={`pi ${icon} ${iconColor}`} />
          <span className="flex-1 font-medium">
            {title} ({count})
          </span>
          <i
            className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-600 transition-transform duration-200`}
          />
        </div>

        <div
          ref={contentRef}
          className="overflow-hidden transition-all ease-in-out"
          style={{
            maxHeight: isExpanded ? maxHeight : '0px',
            opacity: isExpanded ? 1 : 0,
            transitionDuration: `${animationDuration}ms`,
          }}
        >
          {(isExpanded || isAnimating) && (
            <div
              className={`transition-transform ease-in-out ${count > 10 ? 'overflow-y-auto' : ''}`}
              style={{
                transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                maxHeight: count > 10 ? '500px' : 'none',
                transitionDuration: `${animationDuration}ms`,
              }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    );
  },
);
